import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { schnellerfassungAPI } from '@/lib/api';

/* Probetraining von Hand eintragen (Betreiber 24.09.2026): bisher ging das nur
   ueber die Schnellerfassung mit Sprach-/Textnotiz. Gleicher Buchungskern
   (Kapazitaet, Wochenkontingent, Nachbuchungs-Kette, Lead-Uebernahme) —
   nur ohne KI: Person, Kurs, Termin aus der Liste, fertig. Telefonnummer ist
   der Schluessel: bestehender Lead wird wiedererkannt, nicht doppelt angelegt. */
function Feld({ label, value, onChange, type = 'text', placeholder = '', tid }) {
  return <label className="space-y-1 text-xs"><span className="font-semibold uppercase tracking-wider text-muted-foreground">{label}</span><Input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="h-9" data-testid={tid} /></label>;
}

export default function ProbetrainingDialog({ offen, onClose, onFertig, vorbelegt, titel = 'Probetraining eintragen' }) {
  const [f, setF] = useState(null);
  const [kurse, setKurse] = useState([]);
  const [slots, setSlots] = useState(null);
  const [terminId, setTerminId] = useState('');
  const [busy, setBusy] = useState(false);
  /* Teil C (24.09.2026): Hat die Person schon ein Probetraining wahrgenommen,
     antwortet der Server mit 409 + code — das CRM warnt und verlangt eine
     Begründung. Der Funnel bekommt diese Meldung nie zu sehen. */
  const [zweites, setZweites] = useState(null);      // { nachricht, fruehere }
  const [begruendung, setBegruendung] = useState('');
  /* Wochenkontingent (01.10.2026): volle Wochen sieht nur Inhaber/Manager (Server
     entscheidet), markiert — buchen nur mit Begründung, die an der Buchung steht. */
  const [begruendungKontingent, setBegruendungKontingent] = useState('');

  useEffect(() => {
    if (!offen) return;
    setF({ vorname: '', nachname: '', telefon: '', email: '', zielgruppe: 'erwachsen', kind_alter: '', kurs_slug: '', wunsch_datum: '', notiz: '', ...(vorbelegt || {}) });
    setSlots(null); setTerminId('');
    schnellerfassungAPI.kurse().then(r => setKurse(r.data || [])).catch(() => toast.error('Kurse konnten nicht geladen werden'));
  }, [offen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!offen || !f?.kurs_slug) { setSlots(null); return; }
    let aktiv = true;
    schnellerfassungAPI.slots({ kurs_slug: f.kurs_slug, wunsch_datum: f.wunsch_datum || undefined, mit_vollen_wochen: true })
      .then(r => { if (!aktiv) return; const s = r.data || []; setSlots(s); const frei = s.filter(x => !x.kontingent_voll); setTerminId(frei.find(x => x.empfohlen)?.termin_id || frei[0]?.termin_id || ''); })
      .catch(() => { if (aktiv) { setSlots([]); toast.error('Termine konnten nicht geladen werden'); } });
    return () => { aktiv = false; };
  }, [offen, f?.kurs_slug, f?.wunsch_datum]);

  if (!offen || !f) return null;
  const setzen = (k, v) => setF(p => ({ ...p, [k]: v }));

  const buchen = async () => {
    if (!f.telefon.trim()) { toast.error('Ohne Handynummer geht es nicht'); return; }
    if (!f.kurs_slug) { toast.error('Kurs wählen'); return; }
    if (!terminId) { toast.error('Termin wählen'); return; }
    if (zweites && !begruendung.trim()) { toast.error('Bitte eine Begründung für das zweite Probetraining angeben'); return; }
    const kontingentVoll = !!slots?.find(x => x.termin_id === terminId)?.kontingent_voll;
    if (kontingentVoll && !begruendungKontingent.trim()) { toast.error('Wochenkontingent voll — bitte begründen, warum trotzdem gebucht wird'); return; }
    setBusy(true);
    try {
      const r = await schnellerfassungAPI.buchen({
        text: '', vorname: f.vorname.trim(), nachname: f.nachname.trim(), telefon: f.telefon.trim(), email: f.email?.trim() || null,
        zielgruppe: f.zielgruppe, kind_alter: f.zielgruppe === 'kind' && f.kind_alter ? Number(f.kind_alter) : null,
        // Vom Lead aus: „Vorname“ ist oft der des Elternteils, das Kind steht in einem eigenen Feld (auch leer)
        kind_vorname: f.zielgruppe === 'kind' && f.kind_vorname !== undefined ? (f.kind_vorname || '').trim() : null,
        kurs_slug: f.kurs_slug, termin_id: terminId, notiz: f.notiz || '',
        begruendung_zweites: zweites ? begruendung.trim() : null,
        kontingent_ueberstimmen: kontingentVoll, begruendung_kontingent: kontingentVoll ? begruendungKontingent.trim() : null,
      });
      toast.success(`Probetraining gebucht: ${r.data.termin?.label || ''}`);
      setZweites(null); setBegruendung(''); setBegruendungKontingent('');
      onFertig?.(r.data); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'zweites_probetraining') { setZweites(d); toast.warning('Schon ein Probetraining wahrgenommen — Begründung nötig'); }
      else toast.error((typeof d === 'string' && d) || 'Buchung fehlgeschlagen');
    }
    setBusy(false);
  };

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{titel}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Feld label="Vorname" value={f.vorname} onChange={v => setzen('vorname', v)} tid="pt-vorname" />
            <Feld label="Nachname" value={f.nachname} onChange={v => setzen('nachname', v)} tid="pt-nachname" />
            <Feld label="Handynummer" value={f.telefon} onChange={v => setzen('telefon', v)} placeholder="+49 …" tid="pt-telefon" />
            <Feld label="E-Mail (optional)" type="email" value={f.email} onChange={v => setzen('email', v)} tid="pt-email" />
          </div>
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <span className="font-semibold uppercase tracking-wider text-muted-foreground">Für</span>
            {[['erwachsen', 'Erwachsene/n'], ['kind', 'Kind']].map(([k, l]) => (
              <label key={k} className="flex items-center gap-1 cursor-pointer"><input type="radio" name="pt-ziel" className="accent-marke" checked={f.zielgruppe === k} onChange={() => setzen('zielgruppe', k)} /> {l}</label>
            ))}
            {f.zielgruppe === 'kind' && <Input type="number" min={1} max={17} value={f.kind_alter} onChange={e => setzen('kind_alter', e.target.value)} placeholder="Alter" className="h-8 w-20" data-testid="pt-alter" />}
            {f.zielgruppe === 'kind' && f.kind_vorname !== undefined && (
              <label className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Vorname des Kindes</span>
                <Input value={f.kind_vorname} onChange={e => setzen('kind_vorname', e.target.value)} placeholder="falls bekannt" className="h-8 w-36" data-testid="pt-kind-vorname" />
              </label>
            )}
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <label className="space-y-1 text-xs"><span className="font-semibold uppercase tracking-wider text-muted-foreground">Kurs</span>
              <select value={f.kurs_slug} onChange={e => setzen('kurs_slug', e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="pt-kurs">
                <option value="">– Kurs wählen –</option>
                {kurse.map(k => <option key={k.slug} value={k.slug}>{k.name}</option>)}
              </select>
            </label>
            <Feld label="Ab Datum" type="date" value={f.wunsch_datum} onChange={v => setzen('wunsch_datum', v)} tid="pt-datum" />
          </div>
          {f.kurs_slug && (
            <div className="space-y-1.5" data-testid="pt-slots">
              {slots === null ? <p className="text-xs text-muted-foreground">Termine werden geladen…</p>
                : slots.length === 0 ? <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">Keine freien Termine für diesen Kurs in den nächsten drei Wochen. Anderes Datum oder anderen Kurs wählen.</p>
                : slots.map(s => (
                  <label key={s.termin_id} className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer text-sm ${terminId === s.termin_id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                    <input type="radio" name="pt-termin" checked={terminId === s.termin_id} onChange={() => setTerminId(s.termin_id)} />
                    <span className="flex-1">{s.label}</span>
                    {s.empfohlen && <Badge variant="secondary" className="text-[10px]">passt am besten</Badge>}
                    {s.kontingent_voll && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300" data-testid="pt-kontingent-voll">Wochenkontingent voll</Badge>}
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{s.restplaetze} frei</span>
                  </label>
                ))}
            </div>
          )}
          {!!slots?.find(x => x.termin_id === terminId)?.kontingent_voll && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2 text-xs" data-testid="pt-kontingent">
              <p className="font-bold text-amber-800 dark:text-amber-300">⚠ In dieser Woche sind die Probetrainings für diesen Kurs schon vergeben.</p>
              <p className="text-muted-foreground">Als Inhaber/Manager kannst du trotzdem buchen. Die Begründung steht an der Buchung und als Notiz am Lead.</p>
              <Input value={begruendungKontingent} onChange={e => setBegruendungKontingent(e.target.value)} placeholder="Begründung (Pflicht)" className="h-9" data-testid="pt-begruendung-kontingent" />
            </div>
          )}
          <Input value={f.notiz} onChange={e => setzen('notiz', e.target.value)} placeholder="Notiz (optional, landet am Lead)" className="h-9" data-testid="pt-notiz" />
          {zweites && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2 text-xs" data-testid="pt-zweites">
              <p className="font-bold text-amber-800 dark:text-amber-300">⚠ {zweites.nachricht}</p>
              <p className="text-muted-foreground">Nur ein Probetraining pro Person. Wenn es trotzdem sein soll (z. B. anderer Kurs, Verletzung damals), bitte kurz begründen — die Begründung steht an der Buchung.</p>
              <Input value={begruendung} onChange={e => setBegruendung(e.target.value)} placeholder="Begründung (Pflicht)" className="h-9" data-testid="pt-begruendung" />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Abbrechen</Button>
            <Button size="sm" onClick={buchen} disabled={busy || !terminId} className="gap-1.5" data-testid="pt-buchen">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />} Probetraining buchen</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
